/** Mock leads and quick-fill suggestions for terminal flow simulator (Phase 4). */
import { todayISO } from './utils.js';

function isoOffset(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Colombo' });
}

function yymmdd(days = 14) {
  return isoOffset(days).replace(/-/g, '').slice(2);
}

function baseLead(flow) {
  return {
    id: 'sim-lead',
    name: 'Nimal Perera',
    phone: '+94 77 123 4567',
    status: flow.status,
    type: flow.type,
    inquiry_date: todayISO(),
    followup_date: todayISO(),
    amount: null,
    cost_amount: null,
    note: null,
    extra: { pax: { adt: 2, chd: 0, inf: 0, stu: 0 } },
  };
}

const STAGE_MOCK_PRESETS = {
  'ticket.stage.quotation_sent': {
    default: { label: 'Quoted ticket', build: (flow) => {
      const l = baseLead(flow);
      l.amount = 420000;
      l.extra = { pax: { adt: 1, chd: 0, inf: 0, stu: 0 }, quoted_total: 420000 };
      return l;
    }},
    minimal: { label: 'Missing quote total', build: (flow) => {
      const l = baseLead(flow);
      l.amount = null;
      l.extra = { pax: { adt: 1, chd: 0, inf: 0, stu: 0 } };
      return l;
    }},
    multi_pax: { label: '2 ADT + 1 CHD quoted', build: (flow) => {
      const l = baseLead(flow);
      l.amount = 890000;
      l.extra = {
        pax: { adt: 2, chd: 1, inf: 0, stu: 0 },
        quoted_total: 890000,
        travelers: [
          { kind: 'adt', name: 'Nimal Perera', origin: 'CMB', destination: 'DXB' },
          { kind: 'adt', name: 'Kamal Silva', origin: 'CMB', destination: 'DXB' },
        ],
      };
      return l;
    }},
  },
  'ticket.stage.temporary_booking': {
    default: { label: 'Quoted · ready to hold', build: (flow) => {
      const l = baseLead(flow);
      l.amount = 420000;
      l.extra = {
        pax: { adt: 1, chd: 0, inf: 0, stu: 0 },
        quoted_total: 420000,
        travelers: [{ kind: 'adt', name: 'Nimal Perera', origin: 'CMB', destination: 'DXB' }],
      };
      return l;
    }},
    with_pnr: { label: 'PNR already on lead', build: (flow) => {
      const l = STAGE_MOCK_PRESETS['ticket.stage.temporary_booking'].default.build(flow);
      l.pnr = 'XYZZ12';
      l.extra.passport_name = 'Nimal Perera';
      l.extra.passport_no = 'N1234567';
      return l;
    }},
    multi_pax: { label: '2 ADT + 1 CHD · no PNR', build: (flow) => {
      const l = baseLead(flow);
      l.amount = 890000;
      l.extra = {
        pax: { adt: 2, chd: 1, inf: 0, stu: 0 },
        quoted_total: 890000,
        travelers: [
          { kind: 'adt', name: 'Nimal Perera', origin: 'CMB', destination: 'DXB' },
          { kind: 'adt', name: 'Kamal Silva', origin: 'CMB', destination: 'DXB' },
        ],
      };
      return l;
    }},
  },
  'ticket.stage.booking_confirmed': {
    default: { label: 'Advance paid · balance due', build: (flow) => {
      const l = baseLead(flow);
      l.amount = 420000;
      l.extra = {
        quoted_total: 420000,
        advance_paid: 100000,
        balance_due: 320000,
        stage_data: { temporary_booking: { advance: 100000, balance_due: 320000, quoted_total: 420000 } },
      };
      return l;
    }},
    fully_paid: { label: 'Fully paid quote', build: (flow) => {
      const l = baseLead(flow);
      l.amount = 420000;
      l.extra = { quoted_total: 420000, advance_paid: 420000, balance_due: 0 };
      return l;
    }},
  },
  'ticket.stage.ticket_issued': {
    default: { label: 'Confirmed booking', build: (flow) => {
      const l = baseLead(flow);
      l.pnr = 'XYZZ12';
      l.amount = 420000;
      l.extra = { quoted_total: 420000, advance_paid: 420000 };
      return l;
    }},
  },
  'visa.stage.details_provided': {
    default: { label: 'Rates on lead', build: (flow) => {
      const l = baseLead(flow);
      l.package_name = 'India eVisa';
      l.amount = 75000;
      l.extra = {
        pax: { adt: 2, chd: 1, inf: 0, stu: 0 },
        pax_rates: { adt: 25000, chd: 25000 },
        pax_cost_rates: { adt: 18000, chd: 18000 },
      };
      return l;
    }},
    no_rates: { label: 'No sell rates yet', build: (flow) => {
      const l = baseLead(flow);
      l.package_name = 'Malaysia eVisa';
      l.extra = { pax: { adt: 2, chd: 0, inf: 0, stu: 0 } };
      return l;
    }},
    with_inf: { label: '2 ADT + 1 INF', build: (flow) => {
      const l = baseLead(flow);
      l.package_name = 'India eVisa';
      l.amount = 90000;
      l.extra = {
        pax: { adt: 2, chd: 0, inf: 1, stu: 0 },
        pax_rates: { adt: 25000, inf: 15000 },
        pax_cost_rates: { adt: 18000, inf: 12000 },
      };
      return l;
    }},
    stu_mix: { label: '1 ADT + 1 STU', build: (flow) => {
      const l = baseLead(flow);
      l.package_name = 'Malaysia eVisa';
      l.amount = 48000;
      l.extra = {
        pax: { adt: 1, chd: 0, inf: 0, stu: 1 },
        pax_rates: { adt: 28000, stu: 20000 },
        pax_cost_rates: { adt: 20000, stu: 15000 },
      };
      return l;
    }},
  },
  'visa.stage.visa_applied': {
    default: { label: 'Standard visa file', build: (flow) => {
      const l = baseLead(flow);
      l.package_name = 'India eVisa';
      l.extra = { passport_no: 'N1234567' };
      return l;
    }},
  },
  'visa.stage.visa_issued': {
    default: { label: 'Applied visa', build: (flow) => {
      const l = baseLead(flow);
      l.extra = { stage_data: { visa_applied: { application_id: 'APP-001', passport_no: 'N1234567' } } };
      return l;
    }},
  },
  'tour.stage.quotation': {
    default: { label: 'New tour inquiry', build: (flow) => {
      const l = baseLead(flow);
      l.package_name = 'Vietnam';
      const pax = { adt: 2, chd: 1, inf: 0, stu: 0 };
      l.extra = {
        pax,
        tour_quote_request: { date_from: isoOffset(30), pax },
        client_brief: 'Budget 500k, beach hotel',
      };
      return l;
    }},
  },
  'tour.stage.tour_accepted_advance': {
    default: { label: 'Quotation complete', build: (flow) => {
      const l = baseLead(flow);
      l.amount = 580000;
      l.cost_amount = 450000;
      l.extra = {
        pax: { adt: 2, chd: 1, inf: 0, stu: 0 },
        stage_data: {
          quotation: {
            sell_total: 580000,
            cost_total: 450000,
            final_quote: { adt: 200000, chd: 180000 },
            agent_quote: { adt: 150000, chd: 150000 },
          },
        },
      };
      return l;
    }},
  },
  'tour.stage.tour_accepted_progress': {
    default: { label: 'Advance collected', build: (flow) => {
      const l = baseLead(flow);
      l.amount = 580000;
      l.extra = {
        advance_paid: 200000,
        stage_data: {
          tour_accepted_advance: {
            travel_date: isoOffset(45),
            advance_amount: 200000,
            quoted_total: 580000,
            balance_due: 380000,
          },
        },
      };
      return l;
    }},
  },
  'tour.stage.tour_finalised': {
    default: { label: 'Progress complete', build: (flow) => {
      const l = baseLead(flow);
      l.extra = {
        stage_data: {
          tour_accepted_progress: {
            progress_notes: 'CMB-DXB EK 654',
            followup_date: isoOffset(3),
          },
        },
      };
      return l;
    }},
  },
};

/** Scenario presets for new-inquiry flows (Phase 6). Adjusts happy-path answers. */
const NEW_FLOW_PRESETS = {
  'ticket.new': {
    default: {
      label: 'Direct · 1 ADT',
      happy: {},
    },
    transit_2adt_1chd: {
      label: 'Transit · 2 ADT + 1 CHD',
      happy: {
        pref_transit: '2',
        pref_transit_via: 'DOH',
        pref_trip_type: '2',
        pax_adt: '2',
        pax_chd: '1',
        passenger_summary: 'N',
        'traveler:adt:2:name': 'Kamal Silva',
      },
    },
    multi_city: {
      label: 'Multi-city · 1 ADT',
      happy: {
        pref_transit: '3',
        pref_multi_city: 'CMB SIN DXB CMB',
        pax_adt: '1',
        pax_chd: '0',
      },
    },
    stu_return: {
      label: 'Return · 1 ADT + 1 STU',
      happy: {
        pref_transit: '1',
        pref_trip_type: '2',
        pax_adt: '1',
        pax_stu: '1',
        passenger_summary: 'N',
        'traveler:stu:1:name': 'Amaya Fernando',
      },
    },
  },
  'visa.new': {
    default: { label: '2 ADT + 1 CHD', happy: { pax_adt: '2', pax_chd: '1' } },
    single_adt: { label: '1 ADT only', happy: { pax_adt: '1', pax_chd: '0', rate_chd: '-' } },
    family_inf: { label: '2 ADT + 1 INF', happy: { pax_adt: '2', pax_inf: '1', pax_chd: '0' } },
  },
  'tour.new': {
    default: { label: 'Standard Vietnam', happy: {} },
    large_group: { label: '6 ADT group', happy: { pax_adt: '6', pax_chd: '0', pax_inf: '0' } },
    with_chd: { label: '2 ADT + 2 CHD', happy: { pax_line: '2/2/0' } },
    with_inf: { label: '2 ADT + 1 INF', happy: { pax_line: '2/0/1' } },
  },
};

function getPresetMap(flowId) {
  return STAGE_MOCK_PRESETS[flowId] || NEW_FLOW_PRESETS[flowId] || null;
}

function fallbackMockLead(flow) {
  const l = baseLead(flow);
  if (flow.type === 'ticket') {
    l.amount = 420000;
    l.extra.quoted_total = 420000;
  }
  return l;
}

export function listSimulatorMockPresets(flowId) {
  const presets = getPresetMap(flowId);
  if (!presets) return [];
  return Object.entries(presets).map(([id, p]) => ({ id, label: p.label }));
}

export function getPresetHappyOverrides(flowId, presetId = 'default') {
  const presets = getPresetMap(flowId);
  if (!presets) return {};
  const preset = presets[presetId] || presets.default;
  return preset?.happy || {};
}

export function buildSimulatorMockLead(flow, presetId = 'default') {
  const presets = STAGE_MOCK_PRESETS[flow?.id];
  if (!presets) return fallbackMockLead(flow);
  const preset = presets[presetId] || presets.default;
  if (!preset?.build) return fallbackMockLead(flow);
  return preset.build(flow);
}

function matchKey(key, pattern) {
  if (pattern.includes('*')) {
    const re = new RegExp(`^${pattern.replace(/\*/g, '[^:]+')}$`);
    return re.test(key);
  }
  return key === pattern;
}

const HAPPY_ANSWERS = [
  ['name', 'Nimal Perera'],
  ['phone', '0771234567'],
  ['email', '-'],
  ['pref_baggage', '-'],
  ['pref_seat', '-'],
  ['pref_transit', '1'],
  ['pref_transit_via', 'DOH'],
  ['pref_multi_city', 'CMB SIN DXB CMB'],
  ['pref_trip_type', '2'],
  ['pax_adt', '1'],
  ['pax_stu', '0'],
  ['pax_chd', '0'],
  ['pax_inf', '0'],
  ['passenger_summary', 'N'],
  ['add_passenger_type', 'ADT'],
  ['source', '1'],
  ['airline', '-'],
  ['ticket_class', 'economy'],
  ['note', '-'],
  ['package', '1'],
  ['pax_line', '2/1/0'],
  ['brief', 'Budget 500k, beach hotel'],
  ['date_from', isoOffset(30)],
  ['cost_adt', '18000'],
  ['cost_chd', '-'],
  ['sell_adt', '25000'],
  ['sell_chd', '-'],
  ['india_purpose', '1'],
  ['india_duration', '1'],
  ['passport', 'N1234567'],
  ['flight_cost_adt', '-'],
  ['visa_cost_adt', '-'],
  ['add_hotel_rooms', 'N'],
  ['supplier_note', '-'],
  ['progress_notes', 'EK 654 booked'],
  ['agent_rate_adt', '150000'],
  ['agent_rate_chd', '-'],
  ['agent_rate_inf', '-'],
  ['final_rate_adt', '200000'],
  ['final_rate_chd', '180000'],
  ['final_rate_inf', '-'],
  ['quote_price', '75000'],
  ['quote_valid_till', isoOffset(7)],
  ['pnr', 'XYZZ12'],
  ['passport_name', 'Nimal Perera'],
  ['passport_no', 'N1234567'],
  ['booking_expires', `${yymmdd(3)} 1800`],
  ['pay_before', `${yymmdd(2)} 1200`],
  ['advance', '100000'],
  ['paid_on', todayISO()],
  ['payment_amount', '320000'],
  ['remain_pay_by', `${yymmdd(5)} 1200`],
  ['issued_date', todayISO()],
  ['seat_fee', '-'],
  ['seats_booked', 'yes'],
  ['application_id', 'APP-2026-001'],
  ['issue_date', todayISO()],
  ['date_to', isoOffset(37)],
  ['travel_date', isoOffset(45)],
  ['advance_amount', '200000'],
  ['advance_date', todayISO()],
  ['balance_collect_date', isoOffset(40)],
  ['ticket_details', 'CMB-DXB on EK 654'],
  ['followup_date', isoOffset(3)],
  ['ticket_booked_on', todayISO()],
  ['visa_issued_date', isoOffset(12)],
  ['traveler:*:*:gender', 'M'],
  ['traveler:*:*:name', 'Nimal Perera'],
  ['traveler:*:*:same_itinerary', 'Y'],
  ['traveler:*:*:origin', 'CMB'],
  ['traveler:*:*:destination', 'DXB'],
  ['traveler:*:*:depart_date', yymmdd(14)],
  ['traveler:*:*:return_date', yymmdd(21)],
  ['traveler:*:*:depart_time', '1030'],
  ['traveler:*:*:arrive_time', '1430'],
  ['traveler:*:*:transit_stop', 'N'],
  ['traveler:*:*:same_fare', 'Y'],
  ['traveler:*:*:cost', '350000'],
  ['traveler:*:*:profit', '70000'],
  ['traveler:*:*:fare_confirm', 'Y'],
  ['shared:*:cost', '200000'],
  ['shared:*:profit', '30000'],
  ['shared:*:confirm', 'Y'],
];

const INVALID_ANSWERS = [
  ['name', 'A'],
  ['phone', '12'],
  ['email', 'not-an-email'],
  ['pref_transit', '9'],
  ['pref_transit_via', 'XX'],
  ['pref_multi_city', 'CMB'],
  ['pref_trip_type', '9'],
  ['pax_adt', '0'],
  ['pax_stu', '-1'],
  ['passenger_summary', 'maybe'],
  ['add_passenger_type', 'VIP'],
  ['duration', 'bad'],
  ['cost_adt', '-100'],
  ['sell_adt', 'abc'],
  ['quote_price', '-1'],
  ['advance', '-500'],
  ['payment_amount', '0'],
  ['agent_rate_adt', 'xyz'],
  ['final_rate_adt', '-1'],
  ['traveler:*:*:gender', 'X'],
  ['traveler:*:*:origin', 'COLOMBO'],
  ['traveler:*:*:depart_date', 'bad-date'],
  ['traveler:*:*:cost', '-1'],
  ['traveler:*:*:fare_confirm', 'maybe'],
  ['shared:*:confirm', 'no'],
];

function lookupAnswer(key, table, fallback) {
  for (const [pattern, value] of table) {
    if (matchKey(key, pattern)) return value;
  }
  return fallback;
}

function lookupHappyOverride(stepKey, happyOverrides = {}) {
  if (!happyOverrides || !Object.keys(happyOverrides).length) return undefined;
  if (happyOverrides[stepKey] != null) return happyOverrides[stepKey];
  for (const [pattern, value] of Object.entries(happyOverrides)) {
    if (pattern.includes('*') && matchKey(stepKey, pattern)) return value;
  }
  return undefined;
}

export function suggestAnswerForStep(stepKey, questionMeta, mode = 'happy', happyOverrides = {}) {
  if (mode === 'default' && questionMeta?.defaultValue != null && questionMeta.defaultValue !== '') {
    return String(questionMeta.defaultValue);
  }
  if (mode === 'skip') {
    if (questionMeta?.optional || stepKey === '_intro') return '-';
    return null;
  }
  if (mode === 'invalid') {
    return lookupAnswer(stepKey, INVALID_ANSWERS, '!!!');
  }
  if (stepKey === '_intro') return '';
  if (stepKey.startsWith('custom_')) {
    if (mode === 'skip') return questionMeta?.optional ? '-' : null;
    if (mode === 'invalid') return '!!!';
    if (mode === 'happy') return questionMeta?.optional ? '-' : 'Sample answer';
  }
  const presetAnswer = lookupHappyOverride(stepKey, happyOverrides);
  if (presetAnswer != null) return presetAnswer;
  return lookupAnswer(stepKey, HAPPY_ANSWERS, questionMeta?.optional ? '-' : '1');
}

export function pickCapturePreview(data) {
  if (!data) return null;
  const out = {
    name: data.name,
    phone: data.phone,
    email: data.email,
    source: data.source,
    package_name: data.package_name,
    pax: data.pax,
    amount_fields: {
      pax_rates: data.pax_rates,
      pax_cost_rates: data.pax_cost_rates,
      shared_rates: data.shared_rates,
    },
    preferences: data.preferences,
    tour_nights: data.tour_nights,
    tour_days: data.tour_days,
  };
  if (data.travelers?.length) {
    out.travelers = data.travelers.map((t) => ({
      kind: t.kind, name: t.name, origin: t.origin, destination: t.destination,
      cost: t.cost, profit: t.profit,
    }));
  }
  return out;
}
